import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bot, Phone, Calendar, Sparkles, 
  CheckCircle2, TrendingUp,
  Shield, FileText, Star, Activity, Send, Zap, ArrowRight, Users
} from 'lucide-react';

const prompts = [
  { text: "Take care of all my claims for me", icon: FileText, agent: "Alayna", role: "Claim Scrubbing", color: "from-violet-500 to-purple-600", image: "https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png" },
  { text: "Post all my EOBs automatically", icon: CheckCircle2, agent: "Alisha", role: "EOB Posting", color: "from-emerald-500 to-teal-600", image: "/assets/alisha.png" },
  { text: "Run all my insurance verifications", icon: Shield, agent: "Amanda", role: "Insurance Verification", color: "from-blue-500 to-indigo-600", image: "/assets/amanda.png" },
  { text: "Call insurance companies for me", icon: Phone, agent: "Laura", role: "Insurance Calls", color: "from-cyan-500 to-blue-600", image: "https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png" },
  { text: "Call patients to confirm appointments", icon: Calendar, agent: "Amy", role: "Receptionist", color: "from-rose-500 to-pink-600", image: "/assets/amy.png" },
  { text: "Fill my hygiene schedule this week", icon: Users, agent: "Stephanie", role: "Hygiene Recall", color: "from-amber-500 to-orange-600", image: "/assets/stephanie.png" },
  { text: "Handle all denied claims and appeals", icon: TrendingUp, agent: "Sarah", role: "Denial Management", color: "from-indigo-500 to-violet-600", image: "/assets/sarah.png" },
  { text: "Collect on all outstanding balances", icon: Activity, agent: "Julia", role: "A/R Collections", color: "from-green-500 to-emerald-600", image: "/assets/julia.png" },
];

const HeroSection = () => {
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [phase, setPhase] = useState('typing');
  const [agentTasks, setAgentTasks] = useState([]);

  useEffect(() => {
    const prompt = prompts[currentPrompt];
    let charIndex = 0;
    let typingTimer;
    let phaseTimer;

    if (phase === 'typing') {
      setDisplayText('');
      typingTimer = setInterval(() => {
        charIndex++;
        setDisplayText(prompt.text.slice(0, charIndex));
        if (charIndex >= prompt.text.length) {
          clearInterval(typingTimer);
          phaseTimer = setTimeout(() => setPhase('responding'), 600);
        }
      }, 40);
    } else if (phase === 'responding') {
      setAgentTasks(prev => [{ id: Date.now(), agent: prompt.agent, role: prompt.role, text: prompt.text, color: prompt.color, icon: prompt.icon, image: prompt.image }, ...prev].slice(0, 5));
      phaseTimer = setTimeout(() => setPhase('complete'), 1500);
    } else if (phase === 'complete') {
      phaseTimer = setTimeout(() => setPhase('pause'), 2000);
    } else if (phase === 'pause') {
      phaseTimer = setTimeout(() => { setCurrentPrompt(prev => (prev + 1) % prompts.length); setPhase('typing'); }, 500);
    }

    return () => { clearInterval(typingTimer); clearTimeout(phaseTimer); };
  }, [currentPrompt, phase]);

  const currentPromptData = prompts[currentPrompt];

  return (
    <section className="relative pt-[72px] overflow-hidden bg-[#0A0A12]">
      {/* Background gradient layers */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A12] via-[#0d1224] to-[#0A0A12]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-blue-600/8 rounded-full blur-[120px]" />
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-indigo-600/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-16">
        {/* Hero Text - Centered */}
        <div className="text-center mb-14 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-sm mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-medium text-blue-300/90">Introducing a new era of practice management</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-5xl md:text-6xl lg:text-[4.25rem] font-bold leading-[1.08] tracking-tight text-white mb-6"
          >
            The World's 1st{' '}
            <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
              AI-Native Dental PMS
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed mb-10"
          >
            The only PMS that comes with a <span className="text-white font-medium">full team of AI agents</span> to run your office on autopilot.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <Link to="/book-demo" className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base rounded-full transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30">
              Request a Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/roi-calculator" className="inline-flex items-center justify-center px-8 py-4 bg-white/[0.06] hover:bg-white/[0.1] text-white font-semibold text-base rounded-full border border-white/[0.1] hover:border-white/[0.18] transition-all duration-200">
              ROI Calculator
            </Link>
          </motion.div>
        </div>

        {/* AI Command Bar Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="max-w-5xl mx-auto"
        >
          <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm shadow-2xl shadow-black/30">
            {/* Top Bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-white/10" />
                <div className="w-3 h-3 rounded-full bg-white/10" />
                <div className="w-3 h-3 rounded-full bg-white/10" />
              </div>
              <div className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-white/[0.04] text-white/40">
                <Bot className="w-3.5 h-3.5" />
                <span>Asha PMS</span>
                <div className="flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  AI Active
                </div>
              </div>
              <div className="w-16" />
            </div>

            {/* Main Content Area */}
            <div className="p-6 md:p-8">
              {/* AI Command Bar */}
              <div className="mb-8">
                <div className={`relative rounded-xl border overflow-hidden transition-all duration-500 ${
                  phase === 'typing' ? 'border-blue-500/30 shadow-lg shadow-blue-500/5' :
                  phase === 'responding' || phase === 'complete' ? 'border-green-500/30 shadow-lg shadow-green-500/5' :
                  'border-white/[0.08]'
                }`}>
                  <div className="flex items-center gap-3 px-5 py-4 bg-white/[0.03]">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br ${currentPromptData.color}`}>
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-medium mb-1 text-white/30">Ask your AI team anything...</div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm md:text-base font-medium text-white">{displayText}</span>
                        {phase === 'typing' && (
                          <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }} className="w-0.5 h-5 bg-blue-400 inline-block" />
                        )}
                      </div>
                    </div>
                    <motion.div
                      animate={{ scale: phase === 'typing' ? [1, 1.05, 1] : 1, backgroundColor: phase === 'responding' || phase === 'complete' ? '#22c55e' : '#3b82f6' }}
                      transition={{ duration: 0.3 }}
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    >
                      {phase === 'responding' ? (
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}><Zap className="w-4 h-4 text-white" /></motion.div>
                      ) : phase === 'complete' ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <Send className="w-4 h-4 text-white" />
                      )}
                    </motion.div>
                  </div>

                  <AnimatePresence>
                    {(phase === 'responding' || phase === 'complete') && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center gap-3 bg-white/[0.02]">
                          <div className="relative shrink-0">
                            <img src={currentPromptData.image} alt={currentPromptData.agent} className="w-7 h-7 rounded-full object-cover ring-2 ring-blue-400/20" />
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-[#0A0A12]">
                              <Sparkles className="w-2 h-2 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            {phase === 'responding' ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-white">{currentPromptData.agent}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold uppercase tracking-wider flex items-center gap-0.5"><Bot className="w-2.5 h-2.5" /> AI Agent</span>
                                <span className="text-xs text-white/40">is on it...</span>
                                <div className="flex gap-1 ml-1">
                                  {[0, 150, 300].map(delay => <div key={delay} className="w-1 h-1 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: `${delay}ms` }} />)}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-white">{currentPromptData.agent}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold uppercase tracking-wider flex items-center gap-0.5"><Bot className="w-2.5 h-2.5" /> AI Agent</span>
                                <span className="text-xs text-green-400 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Done — handled automatically</span>
                              </div>
                            )}
                          </div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/[0.06] text-white/40">{currentPromptData.role}</div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Bottom Grid */}
              <div className="grid grid-cols-12 gap-6">
                {/* Agent Task Feed */}
                <div className="col-span-12 md:col-span-7">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-white/30">AI Agent Activity Feed</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 font-bold flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> Autonomous</span>
                  </div>
                  <div className="space-y-2.5 min-h-[260px]">
                    <AnimatePresence mode="popLayout">
                      {agentTasks.map((task) => {
                        return (
                          <motion.div key={task.id} initial={{ opacity: 0, x: -20, scale: 0.95 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} layout
                            className="flex items-center gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.03]"
                          >
                            <div className="relative shrink-0">
                              <img src={task.image} alt={task.agent} className="w-8 h-8 rounded-full object-cover ring-2 ring-blue-400/10" />
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-[#0A0A12]">
                                <Sparkles className="w-2 h-2 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-semibold text-white">{task.agent}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-bold uppercase tracking-wider flex items-center gap-0.5"><Bot className="w-2.5 h-2.5" /> AI</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-white/[0.05] text-white/30">{task.role}</span>
                              </div>
                              <div className="text-xs text-white/40">"{task.text}" — <span className="text-green-400 font-medium">completed</span></div>
                            </div>
                            <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                    {agentTasks.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-[260px] rounded-xl border border-dashed border-white/[0.08] text-white/20">
                        <Bot className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-sm">AI agents waiting for instructions...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats Panel */}
                <div className="col-span-12 md:col-span-5 space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-white/30">AI Autopilot Status</span>
                  </div>
                  {[
                    { label: "Tasks Completed Today", value: "847", icon: CheckCircle2, accent: "text-green-400", bg: "bg-green-500/10" },
                    { label: "Calls Handled", value: "124", icon: Phone, accent: "text-blue-400", bg: "bg-blue-500/10" },
                    { label: "Claims Processed", value: "63", icon: FileText, accent: "text-violet-400", bg: "bg-violet-500/10" },
                    { label: "Revenue Captured", value: "$34.2K", icon: TrendingUp, accent: "text-amber-400", bg: "bg-amber-500/10" },
                  ].map((stat, i) => (
                    <motion.div key={stat.label} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 + i * 0.15 }}
                      className="flex items-center gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.03]"
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.bg}`}>
                        <stat.icon className={`w-5 h-5 ${stat.accent}`} />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-wider font-medium text-white/25">{stat.label}</div>
                        <div className="text-lg font-bold text-white">{stat.value}</div>
                      </div>
                      <div className="text-[10px] text-green-400 font-bold flex items-center gap-0.5"><TrendingUp className="w-3 h-3" /> AI</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom gradient fade to next section */}
      <div className="h-24 bg-gradient-to-b from-[#0A0A12] to-white" />
    </section>
  );
};

export default HeroSection;
